import React, { useCallback, useContext, useEffect, useReducer, useState } from "react";
import ReactDOM from "react-dom";
import * as notify from "shared/notify";

import VarietiesTable from "./components/varieties-table";
import { SourcePage, CodingSource } from "shared/taskTypeMap";
import { cacheOrLoad } from "components/attributes/lib/cache";
import { getAttributesByGroup, sendAttributeData } from "components/attributes/lib/attribute-data";
import { reducer } from "./reducer";
import { formatForSave, varietyListMap, varietyWithLinkList } from "./lib/helper";
import { TaskContext } from "contexts/task";

import "./variety-packs.scss";

const getFullAttributes = (uuidList, attributes) => {
    return attributes.filter((attr) => uuidList.includes(attr.uuid));
};

let isPastFirstLoad = false;
const VarietyPacks = (props) => {
    const { task } = useContext(TaskContext);
    const [attributeList, setAttributeList] = useState([]);
    const [varietyData, dispatch] = useReducer(reducer, {});

    const sourcePage = CodingSource[task.taskType] || SourcePage[task.taskType];

    useEffect(() => {
        buildData();

        return () => {
            isPastFirstLoad = false;
        };
    }, []);

    useEffect(() => {
        const saveAttribute = async () => {
            const response = await sendAttributeData(
                { wipKey: task.context.codingWipKey, taskId: task.key, sourcePage: sourcePage },
                {
                    [props.attribute.uuid]: {
                        items: formatForSave(varietyData),
                    },
                }
            );

            if(!response) {
                return false;
            }

            notify.success("Saved");

            if (response && response.triggeredChanges && response.triggeredChanges.updated.hasOwnProperty([props.attribute.uuid])) {
                isPastFirstLoad = false;
                dispatch({ type: "INIT", value: response.triggeredChanges.updated[props.attribute.uuid] });
            }
        };

        if (isPastFirstLoad) {
            saveAttribute();
        } else if (attributeList.length > 0) {
            isPastFirstLoad = true;
        }
    }, [varietyData]);

    const buildData = async () => {
        const parentGroup = props.attribute.additionalInfo.parentGroupInfo.parentGroupName;

        if (parentGroup) {
            const attrList = await cacheOrLoad(parentGroup, getAttributesByGroup);

            setAttributeList(attrList);
        }

        dispatch({ type: "INIT", value: props.defaultValue });
    };

    const addVariety = (varietyName) => {
        dispatch({ type: "ADD_VARIETY", varietyName });
    };

    const removeVariety = async (varietyId) => {
        const confirm = await notify.confirm("Are you sure you want to remove the entire variety?");

        if (confirm) {
            dispatch({ type: "REMOVE_VARIETY", varietyId });
        }
    };

    const updateVariety = (key, value, varietyId) => {
        dispatch({ type: "UPDATE_VARIETY", key, value, varietyId });
    };

    const updateVarietyAttribute = (attr, value, varietyId, fromLink) => {
        const linkedAttributes =
            fromLink && attr.additionalInfo && attr.additionalInfo.linkedAttributes ? attr.additionalInfo.linkedAttributes : null;

        dispatch({
            type: "UPDATE_VARIETY_ATTRIBUTE",
            uuid: attr.uuid,
            value,
            varietyId,
            fromLink,
            linkedAttributes: fromLink ? getFullAttributes(linkedAttributes, attributeList) : [],
        });
    };

    const removeVarietyAttribute = (uuid, varietyId) => {
        dispatch({ type: "REMOVE_VARIETY_ATTRIBUTE", uuid, varietyId });
    };

    const handleClose = useCallback(() => {
        props.updateOnChange(props.attribute.uuid, { items: formatForSave(varietyData) }, props.attribute.componentType);
        props.handleClose();
    });

    return ReactDOM.createPortal(
        <div className="page-takeover">
            <div className="container">
                <VarietiesTable
                    attributeList={attributeList}
                    varieties={varietyListMap(varietyData)}
                    varietiesWithLinks={varietyWithLinkList(varietyData)}
                    varietyData={varietyData}
                    handleClose={handleClose}
                    //varietyAttribute={props.attribute}
                    addVariety={addVariety}
                    removeVariety={removeVariety}
                    updateVariety={updateVariety}
                    updateVarietyAttribute={updateVarietyAttribute}
                    removeVarietyAttribute={removeVarietyAttribute}
                />
            </div>
        </div>,
        document.getElementById("attribute-section")
    );
};

export default VarietyPacks;
